import React, { useMemo, useState } from 'react';
import { closestCenter, DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import { arrayMove, horizontalListSortingStrategy, SortableContext, useSortable } from '@dnd-kit/sortable';
import { Flex, Tag, Tooltip } from 'antd';
import { RedoOutlined } from '@ant-design/icons';
import { isEqual } from 'lodash';

const commonStyle: React.CSSProperties = {
  cursor: 'move',
  transition: 'unset', // Prevent element from shaking after drag
};

interface Item {
  id: number | string;
  text: any;
}

interface DraggableTagsProp {
  items: Item[];
  onDragEnd?: (items: any[]) => void;
  onReset?: (items: any[]) => void;
  closable?: boolean;
  onClose?: (items: any[]) => void;
  labelProp?: string;
  defaultItems?: Item[];
}

export const DraggableTags: React.FC<DraggableTagsProp> = (props: DraggableTagsProp) => {
  const { items, closable, onDragEnd, onReset, labelProp, onClose, defaultItems } = props;

  const sensors = useSensors(useSensor(PointerSensor));

  const [tags, setTags] = useState<Item[]>(items);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) {
      return;
    }
    if (active.id !== over.id) {
      setTags((data) => {
        const oldIndex = data.findIndex((item) => item.id === active.id);
        const newIndex = data.findIndex((item) => item.id === over.id);
        const moved = arrayMove(data, oldIndex, newIndex);
        onDragEnd && onDragEnd(moved);
        return moved;
      });
    }
  };

  const onCloseHandler = (item) => {
    setTags((tags) => {
      const closed = tags.filter((tag) => tag.id !== item.id);
      onClose(closed);
      return closed;
    });
  };

  const onResetHandler = () => {
    setTags(defaultItems);
    onReset && onReset(defaultItems);
  };

  const isChange = useMemo(() => {
    return !isEqual(defaultItems, tags);
  }, [defaultItems, tags]);

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd} collisionDetection={closestCenter}>
      <SortableContext items={tags} strategy={horizontalListSortingStrategy}>
        <Flex gap="4px 0" wrap>
          {tags.map<React.ReactNode>((item) => (
            <DraggableTag
              tag={item}
              key={item.id}
              closable={closable}
              labelProp={labelProp}
              onClose={() => {
                onCloseHandler(item);
              }}
            />
          ))}
          {defaultItems && isChange && (
            <Tooltip title={`リセット: ${defaultItems.map((tag) => (labelProp ? tag.text[labelProp] : tag.text))}`}>
              <RedoOutlined onClick={onResetHandler} />
            </Tooltip>
          )}
        </Flex>
      </SortableContext>
    </DndContext>
  );
};

const DraggableTag = (props) => {
  const { tag, closable, onClose, labelProp } = props;
  const { listeners, transform, transition, isDragging, setNodeRef } = useSortable({ id: tag.id });

  const style = transform
    ? {
        ...commonStyle,
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
        transition: isDragging ? 'unset' : transition, // Improve performance/visual effect when dragging
      }
    : commonStyle;

  return (
    <>
      <Tag closable={closable} onClose={onClose} style={style}>
        <span ref={setNodeRef} {...listeners}>
          {labelProp ? tag.text[labelProp] : tag.text}
        </span>
      </Tag>
    </>
  );
};
